import { useEffect, useState } from 'react';
import { AGENT_PLACEHOLDER, GREETING_MAX, fillGreeting } from '@inbound/contract';

import { useGetLineGreetingQuery, useUpdateLineGreetingMutation } from '../../store/agents-api.js';
import { messageOf } from '../../store/error-message.js';
import { useAppDispatch } from '../../store/hooks.js';
import { pushToast } from '../../ui/toast-store.js';
import { when } from '../../ui/when.js';
import { conflictOf } from './conflict.js';
import { Field } from './Field.js';
import styles from './AgentEditor.module.css';

/**
 * What a caller on one line hears first (S10).
 *
 * Saved on its own, not with the agent: the greeting lives on the telephony
 * preset as that number's override, and the preset has its own version. An empty
 * box means no override, and callers hear the voice preset's own greeting, which
 * is shown so the operator knows what "empty" sounds like.
 */
export function LineGreetingPanel({ lineId }: { lineId: string }): JSX.Element {
  const dispatch = useAppDispatch();
  const { data: greeting, isError, error } = useGetLineGreetingQuery(lineId);
  const [save, saving] = useUpdateLineGreetingMutation();

  // Seeded once per version of the preset, so a save (or a reload after a
  // conflict) shows what is there now, and typing is never overwritten by a
  // background refetch of the same version.
  const [draft, setDraft] = useState<{ version: string | null; text: string } | null>(null);
  useEffect(() => {
    if (greeting === undefined) return;
    setDraft((current) =>
      current !== null && current.version === greeting.presetUpdatedAt
        ? current
        : { version: greeting.presetUpdatedAt, text: greeting.template ?? '' },
    );
  }, [greeting]);

  const title = greeting?.line.label ?? greeting?.line.number ?? 'Line';

  if (isError) {
    return (
      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <h3>Greeting</h3>
        </div>
        <div className={styles.body}>{messageOf(error)}</div>
      </div>
    );
  }

  if (greeting === undefined || draft === null) {
    return (
      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <h3>Greeting</h3>
          <span className={styles.sub}>Loading…</span>
        </div>
      </div>
    );
  }

  const text = draft.text;
  const trimmed = text.trim();
  const changed = trimmed !== (greeting.template ?? '');
  const heard = trimmed === '' ? greeting.presetDefault : fillGreeting(trimmed, greeting.agentName);
  const unfilled = trimmed.includes(AGENT_PLACEHOLDER) && greeting.agentName === null;

  const write = async (template: string | null): Promise<void> => {
    try {
      const saved = await save({
        lineId,
        body: { template, expectedPresetUpdatedAt: greeting.presetUpdatedAt },
      }).unwrap();
      setDraft({ version: saved.presetUpdatedAt, text: saved.template ?? '' });
      dispatch(
        pushToast(
          'success',
          template === null
            ? `${title} uses the voice preset greeting again.`
            : `Saved the greeting of ${title}.`,
        ),
      );
    } catch (failure) {
      // A conflict keeps the operator's text, as the agent form does.
      dispatch(pushToast('error', conflictOf(failure) ?? messageOf(failure)));
    }
  };

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        <h3>Greeting · {title}</h3>
        <span className={styles.sub}>The first thing a caller on this number hears</span>
      </div>

      <div className={styles.body}>
        <Field
          label="Greeting"
          hint={`Write ${AGENT_PLACEHOLDER} for the agent's name. Leave empty to use the voice preset's greeting.`}
          value={text}
          max={GREETING_MAX}
        >
          <textarea
            className={styles.textarea}
            value={text}
            maxLength={GREETING_MAX}
            placeholder={greeting.presetDefault ?? ''}
            aria-label={`Greeting of ${title}`}
            onChange={(event) => setDraft({ ...draft, text: event.target.value })}
          />
        </Field>

        <div className={styles.field}>
          <span className={styles.label}>Callers will hear</span>
          <div className={styles.preview}>
            {heard ?? <em>Nothing is set on the voice preset either.</em>}
          </div>
          {trimmed === '' && heard !== null && (
            <span className={styles.toggleHint}>The voice preset&rsquo;s own greeting.</span>
          )}
          {unfilled && (
            <span className={`${styles.toggleHint} ${styles.warn}`}>
              The agent has no name yet, so {AGENT_PLACEHOLDER} would be read out as written.
            </span>
          )}
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.save}
            disabled={!changed || unfilled || saving.isLoading}
            onClick={() => void write(trimmed === '' ? null : trimmed)}
          >
            {saving.isLoading ? 'Saving…' : 'Save greeting'}
          </button>
          {greeting.template !== null && (
            <button
              type="button"
              className={styles.secondary}
              disabled={saving.isLoading}
              onClick={() => void write(null)}
            >
              Use the voice preset greeting
            </button>
          )}
          <span className={styles.byline}>
            {greeting.updatedAt === null
              ? 'Not changed from this console'
              : `Changed${greeting.updatedBy === null ? '' : ` by ${greeting.updatedBy}`} · ${when(greeting.updatedAt)}`}
          </span>
        </div>
      </div>
    </div>
  );
}
